/**
 * I don't love how we're curently doing this.
 * Essentially, we're adding an empty tooltip container to every
 * place in which there is a tooltip, and showing it on hover.
 * That means that there are a lot of extra shapes (and events) to
 * keep track of.
 *
 * This will be reviewed in WF-581
 */
import { colors, fonts, fontWeights } from 'ui/theme';
import { ShapeEvent } from '../../constants';
import { Group, Shape } from '../../types';
import { ShapeProp, ShapeType, CanvasProp } from '../constants';
import {
  addLinkedShape,
  getClassNameFromShape,
  getItemFromGroup,
  removeLinkedShape,
} from './helpers';
import { optimizeMultilineText, OptimizeMultilineTextArgs } from './textUtils/stringManipulation';
import { getTextDimensions } from './textUtils/textHelpers';
import { drawTextUnderline, getUnderline } from './textUtils/underline';

const TT_ARROW_WIDTH = 0;
const TT_ARROW_HEIGHT = 0;
const TT_OFFSET = 8;
const TT_BACKGROUND_COLOR = 'rgb( 70 70 70)';
const TT_PADDING_HORIZ = 8;
const TT_PADDING_TOP = 6;
const TT_PADDING_BOTTOM = 3;
const TT_RADIUS = 6;
export const MAX_TOOLTIP_WIDTH = 350;

export const tooltipFontConfig = {
  fontWeight: fontWeights.regular,
  fontSize: 12,
  lineHeight: 16,
  fontFamily: fonts.sansSerif,
};

export const tooltipShapeNames = {
  groupSuffix: '-tooltip',
  arrow: 'tooltipArrow',
  container: 'tooltipContainer',
  text: 'tooltipText',
};

const formatTooltipText = (text = '', opts: OptimizeMultilineTextArgs = {}) =>
  optimizeMultilineText(text, {
    maxWidth: MAX_TOOLTIP_WIDTH,
    fontConfig: tooltipFontConfig,
    ...opts,
  });

/**
 * Gets the group to which the tooltip is attached
 */
export const getTooltipParent = (shape: Shape): Group => shape?.getCanvas?.();

export const getTooltipGroupId = (shape: Shape) =>
  `${getClassNameFromShape(shape)}${tooltipShapeNames.groupSuffix}`;

export const getTooltip = (shape: Shape) => getTooltipParent(shape)?.get?.(CanvasProp.Tooltip);

const getTooltipTextHeight = (text = '') => {
  const numLines = text.split('\n').length;
  return tooltipFontConfig.lineHeight * numLines;
};

type ButtonType = 'numUsersExited' | 'numUsersEntered' | 'numUsers';

const userButtonMapper: Record<ButtonType, number> = {
  numUsersExited: 47,
  numUsersEntered: 55,
  numUsers: 42,
};

const getJVTToolTipPadding = (shape: Shape) => {
  const parent = shape.getParent();
  const buttonType = parent.get('buttonType') as ButtonType;
  if (['numUsersExited', 'numUsersEntered', 'numUsers'].includes(buttonType)) {
    const item = getItemFromGroup(parent);
    if (item.hasState('selected')) {
      return userButtonMapper[buttonType];
    } else {
      return 5;
    }
  }
  return 0;
};
const getTooltipDimensions = ({ shape, text }: { shape: Shape; text: string }) => {
  const jvtPadding = getJVTToolTipPadding(shape);
  // As it is attached to the canvas element, it needs to get where the bbox
  // relative to the canvas, not relative to the node
  const bbox = shape.getCanvasBBox();
  const optimizedText = formatTooltipText(text);
  const { width: textWidth } = getTextDimensions(optimizedText, tooltipFontConfig);
  const textHeight = getTooltipTextHeight(optimizedText);
  const centerY = bbox.minY + bbox.height / 2;
  const arrowStartX = bbox.maxX + TT_OFFSET;
  const containerX = arrowStartX + TT_ARROW_WIDTH + jvtPadding - 1; // -1 to make sure there is definitely no gap
  const containerWidth = textWidth + TT_PADDING_HORIZ * 2;
  const containerHeight = textHeight + TT_PADDING_TOP + TT_PADDING_BOTTOM;
  const containerY = centerY - containerHeight / 2;

  const textY = containerY + TT_PADDING_TOP + textHeight / 2;

  return {
    arrow: {
      startX: arrowStartX,
      startY: centerY,
    },
    container: {
      x: containerX,
      y: containerY,
      width: containerWidth,
      height: containerHeight,
    },
    text: {
      x: containerX + TT_PADDING_HORIZ,
      y: textY,
      text: optimizedText,
    },
  };
};

const getArrowPoints = (startX: number, startY: number) => [
  [startX, startY],
  [startX + TT_ARROW_WIDTH, startY + TT_ARROW_HEIGHT],
  [startX + TT_ARROW_WIDTH, startY - TT_ARROW_HEIGHT],
];

const drawTooltipArrow = ({
  group,
  startX,
  startY,
}: {
  group: Group;
  startX: number;
  startY: number;
}) =>
  group.addShape('polygon', {
    name: tooltipShapeNames.arrow,
    className: tooltipShapeNames.arrow,
    draggable: true,
    attrs: {
      fill: TT_BACKGROUND_COLOR,
      points: getArrowPoints(startX, startY),
      shapeType: ShapeType.TooltipArrow,
    },
  });

interface TooltipGroupDetails {
  group: Group;
  x: number;
  y: number;
}

const drawTooltipContainer = ({
  group,
  x,
  y,
  width,
  height,
}: {
  group: Group;
  x: number;
  y: number;
  width: number;
  height: number;
}) => {
  group.addShape('rect', {
    name: 'tooltipContainer',
    className: 'tooltipContainer',
    draggable: true,
    attrs: {
      width,
      height,
      fill: TT_BACKGROUND_COLOR,
      x,
      y,
      radius: TT_RADIUS,
      shadowBlur: 24,
      shadowColor: 'rgb(0 0 0 / 20%)',
      shadowOffsetX: 0,
      shadowOffsetY: 8,
      shapeType: ShapeType.TooltipContainer,
    },
  });
};

const drawTooltipText = ({ group, text, x, y }: TooltipGroupDetails & { text: string }) =>
  group.addShape('text', {
    name: tooltipShapeNames.text,
    className: tooltipShapeNames.text,
    draggable: true,
    attrs: {
      text,
      fill: colors.textOnColor,
      x,
      y,
      textBaseline: 'middle',
      shapeType: ShapeType.TooltipText,
      ...tooltipFontConfig,
    },
  });

export const updateTooltip = ({ shape, tooltipText }: { shape: Shape; tooltipText?: string }) => {
  const tooltip = getTooltip(shape) as Group;

  if (tooltipText) {
    shape.set(ShapeProp.TooltipText, tooltipText);
  }

  if (tooltip) {
    const arrowShape = tooltip.findByClassName(tooltipShapeNames.arrow);
    const containerShape = tooltip.findByClassName(tooltipShapeNames.container);
    const textShape = tooltip.findByClassName(tooltipShapeNames.text);
    const text = tooltipText || textShape.attr().text;
    const dimensions = getTooltipDimensions({ shape, text });

    arrowShape.attr('points', getArrowPoints(dimensions.arrow.startX, dimensions.arrow.startY));
    containerShape.attr({ ...containerShape.attr(), ...dimensions.container });
    textShape.attr({ ...textShape.attr(), ...dimensions.text });
  }
};

/** Hides a visible tooltip */
export const hideTooltip = (shape: Shape) => {
  removeLinkedShape(getTooltipParent(shape), CanvasProp.Tooltip);
};

/** Removes a tooltip from a shape */
export const removeTooltip = (shape: Shape) => {
  shape.emit(ShapeEvent.onTooltipDestroy, { shape });
  hideTooltip(shape);
  shape.set(ShapeProp.TooltipText, null);
};

/** Gets the tooltip text from the shape */
export const getTooltipText = (shape: Shape) => shape.get(ShapeProp.TooltipText);

/**
 * Does the shape have an actively viewed tooltip.
 * IE: Is a tooltip currently visible and associated with the shape?
 */
export const doesShapeHaveVisibleTooltip = (shape: Shape) => {
  const tooltip = getTooltip(shape);
  if (!tooltip) return false;
  const currentTooltipId = tooltip.get('id');
  const shapeTooltipId = getTooltipGroupId(shape);
  return currentTooltipId === shapeTooltipId;
};

/** Does the shape have a tooltip */
export const hasTooltip = (shape: Shape) => !!getTooltipText(shape);

const drawTooltip = (shape: Shape) => {
  const text = shape.get(ShapeProp.TooltipText);

  if (text) {
    /**
     * Adding the tooltip to the canvas so that:
     *  1. The tooltip is always in front of other nodes, even if the target
     *     node is not
     *  2. The tooltip can more easily be hidden on canvas events
     */
    const canvas = getTooltipParent(shape);
    const id = getTooltipGroupId(shape);
    const tooltip = canvas.addGroup({ id, shapeType: ShapeType.Tooltip });
    const dimensions = getTooltipDimensions({ shape, text });

    drawTooltipArrow({ group: tooltip, ...dimensions.arrow });
    drawTooltipContainer({ group: tooltip, ...dimensions.container });
    drawTooltipText({ group: tooltip, ...dimensions.text });
    addLinkedShape(canvas, CanvasProp.Tooltip, tooltip);

    tooltip.toFront();
  }
};

interface AddTooltipOpts {
  /**
   * Shape to add tooltip to.
   * Hovering over this shape will toggle the tooltip by default
   */
  shape: Shape;
  /** The tooltip text */
  text: string;
  /** Should the tooltip be toggled when hovering over the shape? */
  autoToggleIsEnabled?: boolean;
  /** Is the tooltip visible when initilized?  */
  isVisible?: boolean;
  /** Should styles be added? */
  addStyles?: boolean;
}

export const addTooltip = ({
  addStyles = true,
  autoToggleIsEnabled = true,
  isVisible = false,
  shape,
  text,
}: AddTooltipOpts) => {
  shape.set(ShapeProp.TooltipText, text);

  if (isVisible) {
    drawTooltip(shape);
  }

  if (addStyles) {
    shape.attr('cursor', 'pointer');
  }

  if (autoToggleIsEnabled && !isVisible) {
    const showEvents = ['mouseenter'];
    const hideEvents = ['mouseleave', 'mouseout', 'contextmenu', 'dragstart'];
    const destroyEvents = [ShapeEvent.onTooltipDestroy];

    const hide = () => {
      hideTooltip(shape);
      hideEvents.forEach((e) => shape.off(e, hide));
    };
    const show = () => {
      drawTooltip(shape);
      hideEvents.forEach((eName) => shape.on(eName, hide));
    };
    const destroy = () => {
      hide();
      shape.set(ShapeProp.TooltipText, null);
      showEvents.forEach((e) => shape.off(e, show));
      hideEvents.forEach((e) => shape.off(e, hide));
      destroyEvents.forEach((e) => shape.off(e, destroy));
    };

    /**
     * REVIEW: potential future optimization
     * 1. Have the listeners rendered only on the parent, as was done with
     *    anchors in WF-581
     */
    showEvents.forEach((e) => shape.on(e, show));
    destroyEvents.forEach((e) => shape.on(e, destroy, true));
  }
};

/**
 * Removes the tooltip and the underline, if it exists
 */
export const removeTooltipFromText = (shape: Shape) => {
  const underline = getUnderline(shape);
  if (underline && underline.get(ShapeProp.UnderlineType) === 'tooltip') {
    removeLinkedShape(shape, ShapeType.Underline);
  }

  removeTooltip(shape);
};

/**
 * Adds a tooltip and an (optional) underline
 */
export const addTooltipToText = ({
  group,
  isUnderlined = true,
  maxWidth,
  shape,
  tooltipText,
}: {
  group: Group;
  isUnderlined?: boolean;
  maxWidth?: number;
  shape: Shape;
  tooltipText: string;
}) => {
  if (isUnderlined) {
    const underline = drawTextUnderline({ shape, group, maxWidth });
    underline?.set(ShapeProp.UnderlineType, 'tooltip');
  }

  addTooltip({ shape, text: tooltipText });
};
